import React, { useState, useEffect } from "react";
import { X, User, CreditCard, Lock } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import GeneralSettings from "./GeneralSettings";
import BillingSettings from "./BillingSettings";
import SecuritySettings from "./SecuritySettings";
import { updatePageTitle } from "../../../utils/titleUtils";

const SettingsModal = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState("general");
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    updatePageTitle("Settings");
  }, []);

  // Read tab from URL on mount and when URL changes
  useEffect(() => {
    const tabParam = searchParams.get("tab");
    if (tabParam && ["general", "billing", "security"].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  // Update URL when active tab changes
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId });
  };

  if (!isOpen) return null;

  // Get user data from localStorage
  const userData = JSON.parse(localStorage.getItem("user") || "{}");

  const renderContent = () => {
    switch (activeTab) {
      case "general":
        return <GeneralSettings userData={userData} />;
      case "billing":
        return <BillingSettings userData={userData} />;
      case "security":
        return <SecuritySettings userData={userData} />;
      default:
        return <GeneralSettings userData={userData} />;
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-end md:items-center justify-center z-50 p-0 md:p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-bg-50 rounded-t-2xl md:rounded-2xl w-full h-[90vh] md:w-full md:max-w-4xl md:h-[80vh] md:max-h-[80vh] flex flex-col md:flex-row overflow-hidden animate-slide-up md:animate-none">
        {/* Mobile Header */}
        <div className="flex items-center justify-between p-4 border-b border-bd-50 md:hidden">
          <h2 className="text-lg font-semibold text-fg-50">Settings</h2>
          <button
            onClick={onClose}
            className="p-2 text-fg-60 hover:text-fg-50 transition-colors touch-manipulation"
            title="Close settings"
          >
            <X size={24} strokeWidth={1.5} />
          </button>
        </div>

        {/* Mobile Tab Switcher */}
        <div className="flex md:hidden border-b border-bd-50 bg-bg-50 overflow-x-auto">
          {[
            {
              id: "general",
              label: "Account",
              icon: <User size={18} strokeWidth={1.5} />,
            },
            {
              id: "billing",
              label: "Billing",
              icon: <CreditCard size={18} strokeWidth={1.5} />,
            },
            {
              id: "security",
              label: "Security",
              icon: <Lock size={18} strokeWidth={1.5} />,
            },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 whitespace-nowrap border-b-2 transition-colors touch-manipulation ${
                activeTab === tab.id
                  ? "border-ac-02 text-fg-50 font-medium"
                  : "border-transparent text-fg-60 hover:text-fg-50"
              }`}
            >
              {tab.icon}
              <span className="text-sm font-medium">{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Desktop Sidebar */}
        <div className="hidden md:flex w-64 bg-bg-50 overflow-y-auto border-r border-bd-50">
          <div className="pt-4 px-3 w-full">
            <div className="px-2 py-2 space-y-1">
              {[
                {
                  id: "general",
                  label: "Account",
                  icon: <User size={20} strokeWidth={1.5} />,
                },
                {
                  id: "billing",
                  label: "Billing",
                  icon: <CreditCard size={20} strokeWidth={1.5} />,
                },
                {
                  id: "security",
                  label: "Security",
                  icon: <Lock size={20} strokeWidth={1.5} />,
                },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-md hover:bg-bg-40 hover:text-fg-50 transition-colors touch-manipulation ${
                    activeTab === tab.id
                      ? "bg-bg-40 text-fg-50 font-medium"
                      : "text-fg-60"
                  }`}
                >
                  {tab.icon}
                  <span className="text-sm font-medium">{tab.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Close Button - Desktop */}
        <button
          onClick={onClose}
          className="hidden md:flex absolute top-4 right-4 p-2 text-fg-60 hover:text-fg-50 transition-colors z-10 touch-manipulation"
          title="Close settings"
        >
          <X size={24} strokeWidth={1.5} />
        </button>

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto bg-bg-50 px-4 md:px-5 py-4 md:py-5">
          <div className="bg-bg-70 rounded-xl md:rounded-2xl p-4 md:p-6 min-h-full overflow-y-auto">
            <div className="max-w-3xl mx-auto w-full">{renderContent()}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsModal;
